import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import TopUpCelebration from '../components/TopUpCelebration';
import UsageRatesModal from '../components/UsageRatesModal';
import { BillingOverview } from '../components/billing/BillingOverview';
import { RebillingTable } from '../components/billing/RebillingTable';
import { CallLinesAddon } from '../components/concurrency/CallLinesAddon';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../components/ui/sheet';
import '../components/billing/billing.css';
import { toast } from 'sonner';
import {
  CheckCircle,
  AlertCircle,
  Plus,
  RefreshCw,
  Settings,
  ToggleLeft,
  ToggleRight,
  X,
  Archive,
  Zap,
  Filter,
  ChevronDown,
  TrendingUp,
  Users,
  Sliders,
  Infinity,
  Check,
  Clock
} from 'lucide-react';

const BALANCE_PACKAGES = [
  { id: 'small',      amount: 10.00,  price: 10.00, popular: false },
  { id: 'twenty',     amount: 20.00,  price: 20.00, popular: false },
  { id: 'medium',     amount: 50.00,  price: 50.00, popular: true },
  { id: 'large',      amount: 100.00, price: 100.00, popular: false },
  { id: 'enterprise', amount: 500.00, price: 500.00, popular: false },
];

const AUTO_TOPOFF_THRESHOLDS = [
  { value: 1.00, label: '$1.00' },
  { value: 5.00, label: '$5.00' },
  { value: 10.00, label: '$10.00' },
  { value: 25.00, label: '$25.00' },
  { value: 50.00, label: '$50.00' },
];

const BillingPage = () => {
  const { workspaceId } = useParams();
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingError, setBillingError] = useState(false);
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  const [managingSubAccount, setManagingSubAccount] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [loadedWorkspace, setLoadedWorkspace] = useState(null);
  const activeWorkspace = useRef(workspaceId);
  const billingRequest = useRef(0);
  const transactionRequest = useRef(0);
  activeWorkspace.current = workspaceId;

  // Top-up modal
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showRatesModal, setShowRatesModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [customAmount, setCustomAmount] = useState('');

  // Auto top-off
  const [showAutoTopOffModal, setShowAutoTopOffModal] = useState(false);
  const [savedAutoTopOff, setSavedAutoTopOff] = useState({ enabled: false, threshold: 1.00, package_id: 'small', custom_amount: null });
  const [autoTopOff, setAutoTopOff] = useState({ enabled: false, threshold: 1.00, package_id: 'small', custom_amount: null });
  const [savingAutoTopOff, setSavingAutoTopOff] = useState(false);

  // Plan change
  const [planConfig, setPlanConfig] = useState(null);
  const [showPlanChangeModal, setShowPlanChangeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [changingPlan, setChangingPlan] = useState(false);
  const [subAccountsToArchive, setSubAccountsToArchive] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);

  // Beta Program status
  const [betaStatus, setBetaStatus] = useState(null);
  const [buyingLifetime, setBuyingLifetime] = useState(false);
  const [discountSecsLeft, setDiscountSecsLeft] = useState(0);
  const [startingPlanCheckout, setStartingPlanCheckout] = useState(null);

  // Transaction history
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txFilter, setTxFilter] = useState('all');
  // Total spend for currently-applied filters — sub-account + type + date
  // range. Computed server-side across ALL matching records (not just the
  // visible page).
  const [txUsageTotal, setTxUsageTotal] = useState(0);
  const [txUsageCount, setTxUsageCount] = useState(0);

  // Top-up celebration modal
  const [topUpCelebration, setTopUpCelebration] = useState(null);
  const [txTypeFilter, setTxTypeFilter] = useState('all');
  const [txLoading, setTxLoading] = useState(false);
  const [txStartDate, setTxStartDate] = useState('');
  const [txEndDate, setTxEndDate] = useState('');
  const [txPage, setTxPage] = useState(0);
  const [txOpen, setTxOpen] = useState(false);
  const [liveUsage, setLiveUsage] = useState(null);
  const TX_PAGE_SIZE = 20;

  // Rebilling
  const [savingRebilling, setSavingRebilling] = useState(null);
  
  // Credit allocation modal
  const [showAllocateModal, setShowAllocateModal] = useState(null); // sub_account_id
  const [allocateAmount, setAllocateAmount] = useState('');
  const [allocating, setAllocating] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(null);
  const [limitValue, setLimitValue] = useState('');
  const [savingLimit, setSavingLimit] = useState(false);
  
  // Agency markup rates
  const [editingMarkup, setEditingMarkup] = useState(null);
  const [markupForm, setMarkupForm] = useState({ voice_rate: '', chat_rate: '', kb_rate: '' });
  const [savingMarkup, setSavingMarkup] = useState(false);
  const [standardFloor, setStandardFloor] = useState(null);

  // Sub-account auto top-off
  const [showSaAutoTopOff, setShowSaAutoTopOff] = useState(null);
  const [saAutoTopOff, setSaAutoTopOff] = useState({ enabled: false, amount: 1000, period_days: 30 });
  const [savingSaAutoTopOff, setSavingSaAutoTopOff] = useState(false);

  // Cancel subscription
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const closeBillingOverlays = useCallback(() => {
    setShowPlanPicker(false); setShowPlanChangeModal(false); setShowCancelModal(false);
    setShowTopUpModal(false); setShowAutoTopOffModal(false); setShowRatesModal(false);
    setShowAllocateModal(null); setShowLimitModal(null); setShowSaAutoTopOff(null);
    setManagingSubAccount(null); setEditingMarkup(null); setTopUpCelebration(null);
  }, []);

  useEffect(() => {
    closeBillingOverlays(); setLoading(true); setBillingError(false);
    setSelectedPlan(null); setSubAccounts([]); setSubAccountsToArchive([]);
    setTxOpen(false); setTransactions([]); setTxPage(0); setTxFilter('all');
    setTxTypeFilter('all'); setTxStartDate(''); setTxEndDate('');
  }, [workspaceId, closeBillingOverlays]);

  useEffect(() => {
    const closeOnEscape = event => { if (event.key === 'Escape') closeBillingOverlays(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [closeBillingOverlays]);

  useEffect(() => {
    if (workspaceId) {
      fetchBilling();
      fetchPlanConfig();
      fetchSubAccounts();
      fetchLiveUsage();

      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        pollPaymentStatus(sessionId);
      }

      // Lifetime checkout return flow
      const lifetimeFlag = searchParams.get('lifetime');
      if (lifetimeFlag === 'success') {
        fireSuccessConfetti();
        toast.success('🎉 Welcome to LIFETIME access! Your workspace has been upgraded.');
        // Re-fetch beta status so CTA disappears
        setTimeout(() => { fetchPlanConfig(); fetchBilling(); }, 1500);
        // Dispatch the global balance-updated event so the dashboard header
        // refetches /credits/balance instantly — flips BETA MEMBER → LIFETIME
        // badge without waiting for the 120s background poll.
        window.dispatchEvent(new Event('voxinity:balance-updated'));
        setTimeout(() => window.dispatchEvent(new Event('voxinity:balance-updated')), 1800);
      } else if (lifetimeFlag === 'cancelled') {
        toast.info('Lifetime upgrade cancelled. No charge was made.');
      }

      // Monthly plan checkout return flow
      const planFlag = searchParams.get('plan');
      if (searchParams.get('change_plan') === '1') setShowPlanPicker(true);
      if (planFlag === 'success') {
        fireSuccessConfetti();
        toast.success('🎉 Subscription active! Your workspace is fully unlocked.');
        setTimeout(() => { fetchPlanConfig(); fetchBilling(); }, 1500);
      } else if (planFlag === 'cancelled') {
        toast.info('Subscription cancelled. No charge was made.');
      }
    }
  }, [workspaceId, searchParams]);

  // Countdown tick while the post-beta 24h discount is active
  useEffect(() => {
    if (!betaStatus?.discount_active) { setDiscountSecsLeft(0); return; }
    setDiscountSecsLeft(Math.max(0, Number(betaStatus.discount_expires_in_seconds || 0)));
    const id = setInterval(() => {
      setDiscountSecsLeft((s) => {
        if (s <= 1) { clearInterval(id); fetchPlanConfig(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betaStatus?.discount_active, betaStatus?.discount_expires_in_seconds]);

  const fmtCountdown = (secs) => {
    if (!secs || secs <= 0) return null;
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${String(h).padStart(2,'0')}h ${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`;
  };
  const countdownLabel = fmtCountdown(discountSecsLeft);

  const startPlanCheckout = async (planSlug) => {
    setStartingPlanCheckout(planSlug);
    try {
      const res = await api.post('/api/billing/plan-checkout', { plan: planSlug, workspace_id: workspaceId });
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
      else toast.error('Unable to start checkout');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
    } finally {
      setStartingPlanCheckout(null);
    }
  };

  const fireSuccessConfetti = async () => {
    try {
      const mod = await import('canvas-confetti');
      const confetti = mod.default;
      // Quick two-burst celebration matching the top-up confetti style
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.3 }, colors: ['#2dd4bf', '#fbbf24', '#a78bfa', '#f472b6'] });
      setTimeout(() => confetti({ particleCount: 80, spread: 70, origin: { y: 0.4 }, colors: ['#2dd4bf', '#fbbf24'] }), 350);
    } catch { /* confetti optional */ }
  };

  const fetchBilling = async () => {
    const requestId = ++billingRequest.current;
    try {
      const response = await api.get(`/api/workspaces/${workspaceId}/billing`);
      if (activeWorkspace.current !== workspaceId || requestId !== billingRequest.current) return;
      setBillingError(false);
      setLoadedWorkspace(workspaceId);
      setBilling(response.data);
      if (response.data.auto_topoff) {
        setAutoTopOff(response.data.auto_topoff);
        setSavedAutoTopOff(response.data.auto_topoff);
      }
    } catch (error) {
      if (activeWorkspace.current !== workspaceId || requestId !== billingRequest.current) return;
      setBillingError(true);
      setLoadedWorkspace(workspaceId);
      toast.error('Failed to load billing data');
    } finally {
      if (activeWorkspace.current === workspaceId && requestId === billingRequest.current) setLoading(false);
    }
  };

  const fetchPlanConfig = async () => {
    try {
      const response = await api.get('/api/plan-config');
      setPlanConfig(response.data);
    } catch (error) {
      console.error('Failed to load plan config');
    }
    try {
      const bs = await api.get('/api/me/beta-status');
      setBetaStatus(bs.data);
    } catch { /* ignore — endpoint may 401 on guest */ }
  };

  const fetchSubAccounts = async () => {
    try {
      const response = await api.get(`/api/workspaces/${workspaceId}/sub-accounts`);
      if (activeWorkspace.current !== workspaceId) return;
      setSubAccounts(response.data || []);
    } catch (error) {
      console.error('Failed to load sub-accounts');
    }
  };

  const fetchTransactions = async (subAccountId = null, page = 0) => {
    const requestId = ++transactionRequest.current;
    setTxLoading(true);
    try {
      let url = `/api/workspaces/${workspaceId}/balances/transactions?limit=${TX_PAGE_SIZE}&skip=${page * TX_PAGE_SIZE}`;
      if (subAccountId && subAccountId !== 'all') {
        url += `&sub_account_id=${subAccountId}`;
      }
      if (txStartDate) url += `&start_date=${txStartDate}`;
      if (txEndDate) url += `&end_date=${txEndDate}`;
      if (txTypeFilter && txTypeFilter !== 'all') url += `&transaction_type=${txTypeFilter}`;
      const response = await api.get(url);
      if (activeWorkspace.current !== workspaceId || requestId !== transactionRequest.current) return;
      setTransactions(response.data?.transactions || []);
      setTxTotal(response.data?.total || 0);
      setTxUsageTotal(Number(response.data?.total_usage_amount || 0));
      setTxUsageCount(Number(response.data?.total_usage_count || 0));
    } catch (error) {
      console.error('Failed to load transactions');
    } finally {
      setTxLoading(false);
    }
  };

  const handlePlanChange = async () => {
    if (!selectedPlan) return;
    const isPaidSwitch = billing?.has_subscription || selectedPlan.monthly > 0;
    if (isPaidSwitch && subAccountsToArchive.length > 0) {
      setChangingPlan(true);
      try {
        await Promise.all(subAccountsToArchive.map(id => api.put(`/api/sub-accounts/${id}`, { status: 'archived' })));
        fetchSubAccounts();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to archive sub-accounts');
        setChangingPlan(false);
        return;
      }
      setChangingPlan(false);
    }
    if (billing?.has_subscription) {
      await openCustomerPortal();
      return;
    }
    if (selectedPlan.monthly > 0) {
      await startPlanCheckout(selectedPlan.id);
      return;
    }
    setChangingPlan(true);
    try {
      await api.post(`/api/workspaces/${workspaceId}/change-plan`, {
        plan_id: selectedPlan.id,
        archive_sub_accounts: subAccountsToArchive
      });
      toast.success(`Plan changed to ${selectedPlan.name}`);
      setShowPlanChangeModal(false);
      setSelectedPlan(null);
      setSubAccountsToArchive([]);
      fetchBilling();
      fetchSubAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change plan');
    } finally {
      setChangingPlan(false);
    }
  };

  const openPlanChangeModal = (plan) => {
    setShowPlanPicker(false);
    setSelectedPlan(plan);
    setSubAccountsToArchive([]);
    setShowPlanChangeModal(true);
  };

  const openCustomerPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post(`/api/workspaces/${workspaceId}/stripe/customer-portal`);
      if (data.url) window.location.assign(data.url);
      else toast.error('Billing portal is unavailable.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not open the billing portal.');
    } finally { setPortalBusy(false); }
  };

  const getRequiredArchiveCount = () => {
    if (!selectedPlan || !billing) return 0;
    const currentSubAccounts = subAccounts.filter(s => s.status !== 'archived').length;
    const newLimit = selectedPlan.subAccounts || 1;
    return Math.max(0, currentSubAccounts - newLimit);
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      return;
    }
    try {
      const response = await api.get(`/api/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        const amt = typeof response.data.amount_total === 'number'
          ? response.data.amount_total / 100
          : null;
        setTopUpCelebration({ amount: amt, isFirstTopup: !!response.data.is_first_topup });
        fetchBilling();
        fetchTransactions();
        // Signal sidebar + any other balance watchers to refresh immediately
        window.dispatchEvent(new Event('voxinity:balance-updated'));
        window.history.replaceState({}, '', window.location.pathname);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleTopUp = async (pkg) => {
    setSelectedPackage(pkg);
    setProcessingPayment(true);
    try {
      const originUrl = window.location.origin;
      const response = await api.post('/api/payments/checkout', {
        package_id: pkg.id,
        amount: pkg.amount || pkg.price,
        workspace_id: workspaceId,
        origin_url: originUrl
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessingPayment(false);
    }
  };

  const handleSaveAutoTopOff = async () => {
    if (autoTopOff.enabled && autoTopOff.package_id === 'custom') {
      const amt = Number(autoTopOff.custom_amount || 0);
      if (amt < 1 || amt > 9999) {
        toast.error('Custom amount must be between $1 and $9,999');
        return;
      }
    }
    setSavingAutoTopOff(true);
    try {
      await api.put(`/api/workspaces/${workspaceId}/auto-topoff`, autoTopOff);
      if (activeWorkspace.current !== workspaceId) return;
      setSavedAutoTopOff({ ...autoTopOff });
      toast.success(autoTopOff.enabled ? 'Auto top-off enabled' : 'Auto top-off disabled');
      setShowAutoTopOffModal(false);
      fetchBilling();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update auto top-off settings');
    } finally {
      setSavingAutoTopOff(false);
    }
  };

  const handleToggleRebilling = async (subAccountId, currentEnabled) => {
    setSavingRebilling(subAccountId);
    try {
      await api.put(`/api/workspaces/${workspaceId}/sub-accounts/${subAccountId}/rebilling`, {
        rebilling_enabled: !currentEnabled
      });
      toast.success(!currentEnabled ? 'Re-billing enabled' : 'Re-billing disabled');
      fetchBilling();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update re-billing');
    } finally {
      setSavingRebilling(null);
    }
  };

  const handleTxFilterChange = (value) => {
    setTxFilter(value);
    setTxPage(0);
    setTxOpen(true);
    fetchTransactions(value === 'all' ? null : value, 0);
  };

  const handleTxTypeFilterChange = (value) => {
    setTxTypeFilter(value);
    setTxPage(0);
  };

  const handleTxDateApply = () => {
    setTxPage(0);
    fetchTransactions(txFilter === 'all' ? null : txFilter, 0);
  };

  const handleTxDateClear = () => {
    setTxStartDate('');
    setTxEndDate('');
    setTxPage(0);
    setTimeout(() => fetchTransactions(txFilter === 'all' ? null : txFilter, 0), 0);
  };

  const handleTxPageChange = (newPage) => {
    setTxPage(newPage);
    fetchTransactions(txFilter === 'all' ? null : txFilter, newPage);
  };

  const fetchLiveUsage = async () => {
    try {
      const response = await api.get(`/api/workspaces/${workspaceId}/usage/live`);
      if (activeWorkspace.current !== workspaceId) return;
      setLiveUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch live usage');
    }
  };

  const handleTxToggle = () => {
    const next = !txOpen;
    setTxOpen(next);
    if (next && transactions.length === 0) {
      fetchTransactions(txFilter === 'all' ? null : txFilter, 0);
    }
  };

  const handleAllocateBalance = async (subAccountId) => {
    const amount = parseFloat(allocateAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid balance amount');
      return;
    }
    setAllocating(true);
    try {
      await api.post(`/api/workspaces/${workspaceId}/sub-accounts/${subAccountId}/allocate-balances`, { amount });
      toast.success(`Allocated ${amount.toLocaleString()} balance`);
      setShowAllocateModal(null);
      setAllocateAmount('');
      fetchBilling();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allocate balance');
    } finally {
      setAllocating(false);
    }
  };

  const handleSetCreditLimit = async (subAccountId, unlimited = false) => {
    setSavingLimit(true);
    try {
      const payload = unlimited
        ? { unlimited: true }
        : { balance_limit: parseFloat(limitValue) || 0, unlimited: false };
      await api.put(`/api/workspaces/${workspaceId}/sub-accounts/${subAccountId}/balance-allocation`, payload);
      toast.success(unlimited ? 'Set to unlimited' : `Credit limit set to ${parseFloat(limitValue).toLocaleString()}`);
      setShowLimitModal(null);
      setLimitValue('');
      fetchBilling();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update balance limit');
    } finally {
      setSavingLimit(false);
    }
  };

  const handleToggleSubUnlimited = async (subAccountId, enabled) => {
    try {
      await api.put(`/api/workspaces/${workspaceId}/sub-accounts/${subAccountId}/unlimited-credits?enabled=${enabled}`);
      toast.success(enabled ? 'Unlimited balance enabled for sub-account' : 'Unlimited balance disabled for sub-account');
      fetchBilling();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleSaveSaAutoTopOff = async (subAccountId) => {
    setSavingSaAutoTopOff(true);
    try {
      await api.put(`/api/workspaces/${workspaceId}/sub-accounts/${subAccountId}/auto-topoff`, saAutoTopOff);
      toast.success(saAutoTopOff.enabled ? 'Auto top-off enabled for sub-account' : 'Auto top-off disabled');
      setShowSaAutoTopOff(null);
      fetchBilling();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update auto top-off');
    } finally {
      setSavingSaAutoTopOff(false);
    }
  };

  const handleCancelPlan = async () => {
    setCancelling(true);
    try {
      if (billing?.has_subscription) { await openCustomerPortal(); return; }
      await api.post(`/api/workspaces/${workspaceId}/cancel-plan`, { reason: cancelReason });
      toast.success('Subscription cancelled. Your access continues until the end of your billing period.');
      setShowCancelModal(false);
      setCancelReason('');
      fetchBilling();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  // Fetch standard floor rates for markup validation
  const fetchMarkupRates = async () => {
    try {
      const res = await api.get(`/api/workspaces/${workspaceId}/markup-rates`);
      setStandardFloor(res.data.standard_floor);
      if (res.data.current_markup) {
        setMarkupForm({
          voice_rate: res.data.current_markup.voice || '',
          chat_rate: res.data.current_markup.chat || '',
          kb_rate: res.data.current_markup.kb || '',
        });
      }
    } catch (e) { /* ignore */ }
  };

  const handleSaveMarkup = async () => {
    setSavingMarkup(true);
    try {
      const payload = {};
      if (markupForm.voice_rate !== '') payload.voice_rate = parseFloat(markupForm.voice_rate);
      if (markupForm.chat_rate !== '') payload.chat_rate = parseFloat(markupForm.chat_rate);
      if (markupForm.kb_rate !== '') payload.kb_rate = parseFloat(markupForm.kb_rate);
      await api.put(`/api/workspaces/${workspaceId}/markup-rates`, payload);
      toast.success('Markup rates updated');
      setEditingMarkup(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update rates');
    } finally {
      setSavingMarkup(false);
    }
  };

  useEffect(() => {
    if (txStartDate || txEndDate || txTypeFilter !== 'all') {
      fetchTransactions(txFilter === 'all' ? null : txFilter, 0);
    }
  }, [txTypeFilter]);

  // Helper for type badge colors
  const getTypeBadge = (type) => {
    const map = {
      purchase: { bg: 'bg-green-400/10', text: 'text-green-400', label: 'Purchase' },
      payment: { bg: 'bg-emerald-400/10', text: 'text-emerald-400', label: 'Payment' },
      transfer: { bg: 'bg-violet-400/10', text: 'text-violet-400', label: 'Transfer' },
      usage: { bg: 'bg-red-400/10', text: 'text-red-400', label: 'Usage' },
      voice: { bg: 'bg-blue-400/10', text: 'text-blue-400', label: 'Voice' },
      voice_usage: { bg: 'bg-blue-400/10', text: 'text-blue-400', label: 'Voice' },
      voice_ai: { bg: 'bg-teal-400/10', text: 'text-teal-400', label: 'Voice AI' },
      voice_ai_usage: { bg: 'bg-teal-400/10', text: 'text-teal-400', label: 'Voice AI' },
      voice_pending: { bg: 'bg-amber-400/10', text: 'text-amber-400', label: 'Voice pending' },
      chat: { bg: 'bg-cyan-400/10', text: 'text-cyan-400', label: 'Chat' },
      chat_usage: { bg: 'bg-cyan-400/10', text: 'text-cyan-400', label: 'Chat' },
      bonus: { bg: 'bg-amber-400/10', text: 'text-amber-400', label: 'Bonus' },
      welcome_bonus: { bg: 'bg-teal-400/10', text: 'text-teal-400', label: 'Welcome credit' },
      refund: { bg: 'bg-blue-400/10', text: 'text-blue-400', label: 'Refund' },
    };
    const m = map[type] || { bg: 'bg-gray-400/10', text: 'text-gray-400', label: type || 'Other' };
    return <span className={`text-xs px-2 py-0.5 rounded-full ${m.bg} ${m.text}`}>{m.label}</span>;
  };

  const txTotalPages = Math.ceil(txTotal / TX_PAGE_SIZE);

  if (loading || loadedWorkspace !== workspaceId) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-10 h-10 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const balanceBalance = billing?.balance || 0;

  if (billingError || !billing) return <DashboardLayout><div className="billing-modern p-6 md:p-8"><section className="billing-error" role="alert" data-testid="billing-load-error"><h1>Billing could not be loaded</h1><p className="billing-muted">Your balance and subscription have not been changed.</p><button className="billing-button" onClick={fetchBilling} data-testid="billing-retry-button">Try again</button></section></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="billing-modern p-4 sm:p-6 md:p-8" data-testid="billing-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-2xl md:text-3xl font-display font-semibold text-white mb-1">Billing</h1>
          <p className="text-gray-400">Manage your balance, subscription, and payment settings</p>
        </motion.div>

        <BillingOverview billing={billing} subAccounts={subAccounts} autoTopOff={savedAutoTopOff} packages={BALANCE_PACKAGES}
          onPlans={() => setShowPlanPicker(true)} onCancel={() => setShowCancelModal(true)} onTopUp={() => setShowTopUpModal(true)}
          onAutoPay={() => { setAutoTopOff({ ...savedAutoTopOff }); setShowAutoTopOffModal(true); }} onPortal={openCustomerPortal} portalBusy={portalBusy}
          onRates={() => setShowRatesModal(true)} onHistory={() => { setTxOpen(true); fetchTransactions(); setTimeout(() => document.querySelector('[data-testid="transaction-history"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); }}
          canChangePlan={!!planConfig?.plans && !billing.is_lifetime && !billing.is_legacy_plan && !(billing.is_beta && !betaStatus?.program_ended)} />

        <CallLinesAddon workspaceId={workspaceId} />

        {/* Post-beta 24h discount urgency banner */}
        {betaStatus?.discount_active && !billing?.is_lifetime && countdownLabel && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative mb-6 rounded-2xl p-5 border-2 overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(251,191,36,0.18), rgba(245,158,11,0.05))',
              borderColor: 'rgba(251,191,36,0.6)',
              boxShadow: '0 0 60px -10px rgba(251,191,36,0.4), inset 0 0 0 1px rgba(251,191,36,0.2)',
            }}
            data-testid="billing-discount-urgency-banner"
          >
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-40 pointer-events-none"
                 style={{ background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)' }} />
            <div className="relative flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 animate-pulse"
                     style={{ background: 'linear-gradient(135deg,#fbbf24,#f59e0b)' }}>
                  <Clock className="text-[#0a0b0f]" size={22} />
                </div>
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 mb-1 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold tracking-[0.2em] bg-amber-400/30 text-amber-100 border border-amber-400/50">LAST CHANCE DEAL</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider border-2 border-amber-400/60 bg-amber-400/20 text-amber-100 animate-pulse">
                      SAVE $2,000
                    </span>
                  </div>
                  <h4 className="text-lg font-semibold text-white">
                    Lock in <b className="text-amber-300">LIFETIME</b> at <b className="text-amber-300">{betaStatus?.lifetime_price_label || '$3,000'}</b> before it jumps to <s className="opacity-70">$5,000</s>
                  </h4>
                  <p className="text-sm text-amber-100/80 mt-1">
                    One-time payment. Never pay a monthly fee again. All monthly plans also discounted during this window.
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-center lg:items-end gap-3 flex-shrink-0">
                <div className="text-center lg:text-right">
                  <div className="text-[10px] font-bold tracking-widest text-amber-200/70 mb-0.5">EXPIRES IN</div>
                  <div className="text-2xl font-bold text-amber-200 tabular-nums font-mono">{countdownLabel}</div>
                </div>
                <button
                  onClick={async () => {
                    setBuyingLifetime(true);
                    try {
                      const res = await api.post('/api/billing/lifetime-checkout');
                      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
                      else toast.error('Unable to start checkout — please contact support.');
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Checkout failed. Please try again or contact support.');
                    } finally {
                      setBuyingLifetime(false);
                    }
                  }}
                  disabled={buyingLifetime}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold disabled:opacity-50 whitespace-nowrap transition-transform hover:scale-[1.03]"
                  style={{ background: 'linear-gradient(135deg,#fbbf24,#f59e0b)', color: '#0a0b0f', boxShadow: '0 8px 24px -6px rgba(251,191,36,0.6)' }}
                  data-testid="buy-lifetime-btn"
                >
                  {buyingLifetime ? 'Opening checkout…' : `Secure LIFETIME (${betaStatus?.lifetime_price_label || '$3,000'})`}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Standalone LIFETIME CTA — shown ONLY when NOT in the 24h discount
            window (e.g. active Beta members before program ends). During the
            24h window the combined urgency banner above owns the CTA. */}
        {billing?.is_beta && !billing?.is_lifetime && !betaStatus?.discount_active && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="relative rounded-2xl p-6 mb-6 border-2 overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(251,191,36,0.12), rgba(245,158,11,0.04))',
              borderColor: 'rgba(251,191,36,0.5)',
              boxShadow: '0 0 40px -10px rgba(251,191,36,0.35)',
            }}
            data-testid="beta-lifetime-cta"
          >
            <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full blur-3xl opacity-30 pointer-events-none"
                 style={{ background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)' }} />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                     style={{ background: 'linear-gradient(135deg,#fbbf24,#f59e0b)' }}>
                  <CheckCircle className="text-[#0a0b0f]" size={22} />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-lg font-semibold text-white">Beta Members only: LIFETIME for {betaStatus?.lifetime_price_label || '$3,000'}</h4>
                    {!betaStatus?.program_ended && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider border"
                            style={{ background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)', color: '#0a0b0f', borderColor: 'rgba(148,163,184,0.5)' }}>
                        BETA MEMBERS
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-amber-100/80 mt-1">
                    One-time payment. Never pay a monthly fee again.
                    {!betaStatus?.program_ended && <span> Price jumps to $5,000 once Beta ends.</span>}
                  </p>
                </div>
              </div>
              <button
                onClick={async () => {
                  setBuyingLifetime(true);
                  try {
                    const res = await api.post('/api/billing/lifetime-checkout');
                    if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
                    else toast.error('Unable to start checkout — please contact support.');
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Checkout failed. Please try again or contact support.');
                  } finally {
                    setBuyingLifetime(false);
                  }
                }}
                disabled={buyingLifetime}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold disabled:opacity-50 whitespace-nowrap transition-transform hover:scale-[1.03]"
                style={{ background: 'linear-gradient(135deg,#fbbf24,#f59e0b)', color: '#0a0b0f', boxShadow: '0 8px 24px -6px rgba(251,191,36,0.5)' }}
                data-testid="buy-lifetime-btn-static"
              >
                {buyingLifetime ? 'Opening checkout…' : `Secure LIFETIME (${betaStatus?.lifetime_price_label || '$3,000'})`}
              </button>
            </div>
          </motion.div>
        )}

        {/* Plan Change Options — HIDDEN for active beta users (locked until lifetime or beta ends) */}
        {planConfig?.plans && billing?.payment_status !== 'lifetime' && !(billing?.is_beta && !betaStatus?.program_ended) && (
          <Dialog open={showPlanPicker} onOpenChange={setShowPlanPicker}><DialogContent className="billing-modern billing-plan-picker" data-testid="billing-plan-picker"><DialogTitle>Choose your plan</DialogTitle><DialogDescription>Subscriptions and usage are billed separately.</DialogDescription>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-2xl p-6 mb-6 relative overflow-hidden"
            style={betaStatus?.discount_active ? {
              borderColor: 'rgba(251,191,36,0.35)',
              boxShadow: '0 0 50px -15px rgba(251,191,36,0.35)',
            } : {}}
          >
            {betaStatus?.discount_active && (
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-20 pointer-events-none"
                   style={{ background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)' }} />
            )}
            <div className="flex items-center justify-between mb-4 relative">
              <h4 className="text-sm font-medium text-gray-300">Change Plan</h4>
              {betaStatus?.discount_active && countdownLabel && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-bold tracking-wider bg-amber-400/15 border border-amber-400/40 text-amber-200">
                  <Clock size={11} className="animate-pulse" />
                  DISCOUNTED · {countdownLabel}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 relative">
              {[...planConfig.plans]
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(plan => {
                  const isCurrentPlan = plan.id === 'launchpad' ? billing?.is_free_plan : plan.id === billing?.current_plan?.id;
                  const discountInfo = plan.id !== 'launchpad' && betaStatus?.discount_active ? (betaStatus?.plan_prices?.[plan.id]) : null;
                  const discountActive = !!discountInfo?.is_discounted;
                  const discAmt = discountActive ? Math.round((discountInfo.amount_cents || 0) / 100) : null;
                  const regAmt = discountActive ? Math.round((discountInfo.regular_cents || 0) / 100) : null;
                  const savings = discountActive ? Math.max(0, regAmt - discAmt) : 0;
                  // If discount is active AND plan has a valid checkout slug, use startPlanCheckout; otherwise fall back to modal
                  const canDiscountCheckout = !billing?.has_subscription && discountActive && ['starter','agency','pro'].includes(plan.id);
                  const clickHandler = () => {
                    if (isCurrentPlan) return;
                    if (canDiscountCheckout) startPlanCheckout(plan.id);
                    else openPlanChangeModal(plan);
                  };
                  return (
                    <button
                      key={plan.id}
                      onClick={clickHandler}
                      disabled={isCurrentPlan || startingPlanCheckout === plan.id}
                      className={`p-4 rounded-xl text-left transition-all relative overflow-hidden group ${
                        isCurrentPlan
                          ? 'bg-teal-400/10 border-2 border-teal-400/50 cursor-default'
                          : discountActive
                          ? 'bg-gradient-to-b from-amber-400/[0.08] to-transparent border-2 border-amber-400/40 hover:border-amber-400/70 hover:shadow-[0_0_30px_-8px_rgba(251,191,36,0.5)]'
                          : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20'
                      }`}
                      data-testid={`plan-card-${plan.id}`}
                    >
                      {discountActive && savings > 0 && (
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider bg-amber-400/25 text-amber-100 border border-amber-400/50">
                          SAVE ${savings}
                        </div>
                      )}
                      <p className={`text-sm font-semibold ${isCurrentPlan ? 'text-teal-400' : 'text-white'}`}>
                        {plan.name}
                      </p>
                      <div className="mt-1 flex items-baseline gap-1.5">
                        {discountActive ? (
                          <>
                            <span className="text-xl font-bold text-amber-200 tabular-nums">${discAmt}</span>
                            <s className="text-xs text-slate-500 tabular-nums">${regAmt}</s>
                            <span className="text-[10px] text-slate-400">/mo</span>
                          </>
                        ) : (
                          <span className="text-sm text-gray-400">{plan.monthly > 0 ? `$${plan.monthly}/mo` : '$0/month'}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {plan.subAccounts === 999 ? 'Unlimited' : plan.subAccounts} accounts
                      </p>
                      {isCurrentPlan && (
                        <span className="mt-2 inline-flex items-center gap-1 text-xs text-teal-400">
                          <CheckCircle size={12} /> Current
                        </span>
                      )}
                      {startingPlanCheckout === plan.id && (
                        <span className="mt-2 inline-flex items-center gap-1 text-xs text-amber-300">Opening checkout…</span>
                      )}
                    </button>
                  );
                })}
            </div>
            <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between relative">
              <p className="text-xs text-gray-500">
                {betaStatus?.discount_active
                  ? 'Discounted pricing locks in at checkout. Offer expires with the timer above.'
                  : 'Paid changes are confirmed securely before your subscription changes.'}
              </p>
            </div>
          </motion.div>
          </DialogContent></Dialog>
        )}

        {/* Re-billing Section */}
        <RebillingTable accounts={billing?.sub_account_billing || []} onManage={setManagingSubAccount} unlimited={billing?.unlimited || billing?.unlimited_credits} />
        <Sheet open={!!managingSubAccount} onOpenChange={open => { if (!open) { setManagingSubAccount(null); setEditingMarkup(null); } }}>
        <SheetContent className="billing-modern billing-manage-drawer" data-testid="rebilling-manage-drawer" onCloseAutoFocus={event => { if (showAllocateModal || showLimitModal || showSaAutoTopOff) event.preventDefault(); }}>
        <SheetTitle>Manage sub-account billing</SheetTitle><SheetDescription>Changes apply to the selected account.</SheetDescription>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="pt-6"
          data-testid="rebilling-detail-controls"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-400/10 flex items-center justify-center">
                <Sliders className="text-violet-400" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Sub-Account Re-billing</h3>
                <p className="text-sm text-gray-400">Control how sub-accounts are billed for usage</p>
              </div>
            </div>
          </div>

          <div className="mb-4 p-4 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-amber-400 flex-shrink-0 mt-0.5" size={16} />
              <div className="text-sm text-gray-300">
                <p className="mb-1"><span className="text-amber-400 font-medium">Re-billing OFF:</span> Usage is charged to your agency balance balance.</p>
                <p><span className="text-teal-400 font-medium">Re-billing ON:</span> Sub-account manages their own billing. You can set custom markup rates.</p>
              </div>
            </div>
          </div>

          {billing?.sub_account_billing?.length > 0 ? (
            <div className="space-y-3">
              {billing.sub_account_billing.filter(sa => sa.sub_account_id === managingSubAccount).map((sa) => (
                <div key={sa.sub_account_id} className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex w-full min-w-0 items-center gap-3 sm:flex-1">
                      <div className="w-8 h-8 shrink-0 rounded-lg bg-teal-400/10 flex items-center justify-center">
                        <Users className="text-teal-400" size={14} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white break-words" data-testid={`managed-account-name-${sa.sub_account_id}`}>{sa.name}</p>
                        <p className="text-xs text-gray-500 break-all">{sa.sub_account_id}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      {sa.rebilling_enabled && sa.rebilling_rate && (
                        <span className="text-xs text-gray-400 bg-white/5 px-2 py-1 rounded">
                          Markup: {sa.rebilling_rate}x
                        </span>
                      )}
                      <button
                        onClick={() => handleToggleRebilling(sa.sub_account_id, sa.rebilling_enabled)}
                        disabled={savingRebilling === sa.sub_account_id}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          sa.rebilling_enabled
                            ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30'
                            : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                        }`}
                        data-testid={`rebilling-toggle-${sa.sub_account_id}`}
                      >
                        {savingRebilling === sa.sub_account_id ? (
                          <RefreshCw className="animate-spin" size={12} />
                        ) : sa.rebilling_enabled ? (
                          <ToggleRight size={16} />
                        ) : (
                          <ToggleLeft size={16} />
                        )}
                        {sa.rebilling_enabled ? 'Re-bill On' : 'Re-bill Off'}
                      </button>
                    </div>
                  </div>

                  {/* Markup Rate Controls — shown when re-billing is ON */}
                  {sa.rebilling_enabled && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-gray-400 flex items-center gap-1.5">
                          <TrendingUp size={12} /> Sub-account balance rates
                        </p>
                        {editingMarkup !== sa.sub_account_id && (
                          <button
                            onClick={() => { setEditingMarkup(sa.sub_account_id); fetchMarkupRates(); }}
                            className="text-[10px] text-teal-400 hover:underline"
                            data-testid={`edit-markup-${sa.sub_account_id}`}
                          >
                            Set Markup
                          </button>
                        )}
                      </div>
                      {editingMarkup === sa.sub_account_id && standardFloor && (
                        <div className="space-y-2 mt-2">
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="text-[9px] uppercase tracking-wider text-gray-500">Voice ($/min)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={markupForm.voice_rate}
                                onChange={e => setMarkupForm(p => ({ ...p, voice_rate: e.target.value }))}
                                placeholder={`Min: ${standardFloor?.voice?.eleven_flash_v2_5 || 0.12}`}
                                className="w-full mt-1 px-2 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-teal-400/50"
                                data-testid="markup-voice-rate"
                              />
                              <p className="text-[9px] text-gray-600 mt-0.5">Floor: ${standardFloor?.voice?.eleven_flash_v2_5 || 0.12}</p>
                            </div>
                            <div>
                              <label className="text-[9px] uppercase tracking-wider text-gray-500">Chat ($/msg)</label>
                              <input
                                type="number"
                                step="0.001"
                                value={markupForm.chat_rate}
                                onChange={e => setMarkupForm(p => ({ ...p, chat_rate: e.target.value }))}
                                placeholder={`Min: ${standardFloor?.chat?.['gpt-4o-mini'] || 0.025}`}
                                className="w-full mt-1 px-2 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-teal-400/50"
                                data-testid="markup-chat-rate"
                              />
                              <p className="text-[9px] text-gray-600 mt-0.5">Floor: ${standardFloor?.chat?.['gpt-4o-mini'] || 0.025}</p>
                            </div>
                            <div>
                              <label className="text-[9px] uppercase tracking-wider text-gray-500">KB ($/query)</label>
                              <input
                                type="number"
                                step="0.001"
                                value={markupForm.kb_rate}
                                onChange={e => setMarkupForm(p => ({ ...p, kb_rate: e.target.value }))}
                                placeholder={`Min: ${standardFloor?.kb || 0.005}`}
                                className="w-full mt-1 px-2 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-teal-400/50"
                                data-testid="markup-kb-rate"
                              />
                              <p className="text-[9px] text-gray-600 mt-0.5">Floor: ${standardFloor?.kb || 0.005}</p>
                            </div>
                          </div>
                          {/* BYOK note — explicitly NOT a markup field. BYOK
                              chats are billed by Voxinity at a flat
                              $0.001/msg regardless of agency markup
                              settings. Surfacing it here keeps the agency
                              owner informed that BYOK savings flow
                              straight to the sub-account. */}
                          <p className="text-[10px] text-amber-300/80 bg-amber-400/[0.06] border border-amber-400/15 rounded-lg px-2 py-1.5 leading-snug">
                            <strong>BYOK chats</strong> are billed at a flat <strong>$0.001/msg</strong> by Voxinity — not marked up. Sub-accounts with their own OpenAI / Claude key automatically pay this lower rate.
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleSaveMarkup}
                              disabled={savingMarkup}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-teal-400/10 text-teal-400 border border-teal-400/20 hover:bg-teal-400/20 transition-colors disabled:opacity-50"
                              data-testid="save-markup-btn"
                            >
                              {savingMarkup ? <RefreshCw className="animate-spin" size={10} /> : <Check size={10} />}
                              Save Rates
                            </button>
                            <button
                              data-testid="cancel-markup-button"
                              onClick={() => setEditingMarkup(null)}
                              className="px-3 py-1.5 rounded-lg text-xs text-gray-400 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                            >
                              Cancel
                            </button>
                            <p className="text-[9px] text-gray-500 ml-auto">Rates can't go below standard pricing</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Credit Allocation Controls (shown when re-billing is OFF) */}
                  {!sa.rebilling_enabled && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      {/* Per-sub-account unlimited toggle (only for admin workspaces: Skye AI & Tecartemis) */}
                      {(billing?.unlimited_credits || billing?.unlimited_balances || billing?.unlimited) && (billing?.is_admin_workspace) && (
                        <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/5">
                          <div className="flex items-center gap-2">
                            <Infinity size={14} className={sa.unlimited_credits_enabled !== false ? 'text-teal-400' : 'text-gray-500'} />
                            <span className="text-xs text-gray-400">Unlimited Balance</span>
                          </div>
                          <button
                            onClick={() => handleToggleSubUnlimited(sa.sub_account_id, !sa.unlimited_credits_enabled)}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                              sa.unlimited_credits_enabled !== false
                                ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30'
                                : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                            }`}
                            data-testid={`sub-unlimited-toggle-${sa.sub_account_id}`}
                          >
                            {sa.unlimited_credits_enabled !== false ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                            {sa.unlimited_credits_enabled !== false ? 'On' : 'Off'}
                          </button>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-gray-500">Allocation:</span>
                          {sa.balance_unlimited ? (
                            <span className="text-amber-400 font-medium bg-amber-400/10 px-2 py-0.5 rounded">Unlimited</span>
                          ) : sa.balance_limit != null ? (
                            <span className="text-white font-medium">{Number(sa.balance_limit).toLocaleString()} cap</span>
                          ) : (
                            <span className="text-gray-400">No limit set</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-gray-500">Allocated:</span>
                          <span className="text-amber-400 font-medium">${(sa.allocated_balances || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex items-center gap-2 ml-auto">
                          <button
                            onClick={() => { setManagingSubAccount(null); setShowAllocateModal(sa.sub_account_id); setAllocateAmount(''); }}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-400/10 text-amber-400 border border-amber-400/20 hover:bg-amber-400/20 transition-colors"
                            data-testid={`allocate-balances-${sa.sub_account_id}`}
                          >
                            <Plus size={12} /> Add Balance
                          </button>
                          <button
                            onClick={() => { setManagingSubAccount(null); setShowLimitModal(sa.sub_account_id); setLimitValue(sa.balance_limit != null ? String(sa.balance_limit) : ''); }}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10 transition-colors"
                            data-testid={`set-limit-${sa.sub_account_id}`}
                          >
                            <Settings size={12} /> Set Limit
                          </button>
                          <button
                            onClick={() => {
                              setShowSaAutoTopOff(sa.sub_account_id);
                              setManagingSubAccount(null);
                              setSaAutoTopOff(sa.auto_topoff || { enabled: false, amount: 1000, period_days: 30 });
                            }}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                              sa.auto_topoff?.enabled
                                ? 'bg-teal-400/10 text-teal-400 border-teal-400/20'
                                : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                            }`}
                            data-testid={`sa-auto-topoff-${sa.sub_account_id}`}
                          >
                            <RefreshCw size={12} /> Auto
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="mx-auto text-gray-600 mb-3" size={32} />
              <p className="text-gray-400">No sub-accounts found</p>
              <p className="text-sm text-gray-500 mt-1">Create a sub-account to manage re-billing settings</p>
            </div>
          )}
        </motion.div>
        </SheetContent></Sheet>

        {/* Transaction History — Collapsible */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass-card rounded-xl overflow-hidden"
          data-testid="transaction-history"
        >
          {/* Header — always visible, acts as toggle */}
          <button
            onClick={handleTxToggle}
            className="w-full px-6 py-5 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
            data-testid="tx-toggle"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-400/10 flex items-center justify-center">
                <TrendingUp className="text-teal-400" size={20} />
              </div>
              <div className="text-left">
                <h3 className="text-lg font-semibold text-white">Transaction History</h3>
                <p className="text-sm text-gray-400">
                  {txTotal > 0 ? `${txTotal} total records` : 'Click to view balance purchases, usage, and transfers'}
                </p>
              </div>
            </div>
            <ChevronDown size={18} className={`text-gray-400 transition-transform duration-200 ${txOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Body — only when open */}
          {txOpen && (
            <div className="px-6 pb-6">
              {/* Filters Row */}
              <div className="flex flex-wrap items-end gap-3 mb-5 p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase tracking-wider text-gray-500">Account</label>
                  <div className="relative">
                    <select
                      value={txFilter}
                      onChange={(e) => handleTxFilterChange(e.target.value)}
                      className="appearance-none px-3 py-2 pr-8 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-teal-400/50 min-w-[140px]"
                      data-testid="tx-filter-select"
                    >
                      <option value="all">All accounts</option>
                      {subAccounts.filter(s => s.status !== 'archived').map(sa => (
                        <option key={sa.sub_account_id} value={sa.sub_account_id}>{sa.name}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase tracking-wider text-gray-500">Type</label>
                  <div className="relative">
                    <select
                      value={txTypeFilter}
                      onChange={(e) => handleTxTypeFilterChange(e.target.value)}
                      className="appearance-none px-3 py-2 pr-8 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-teal-400/50 min-w-[120px]"
                      data-testid="tx-type-filter"
                    >
                      <option value="all">All types</option>
                      <option value="voice">Voice Calls</option>
                      <option value="voice_ai" data-testid="billing-filter-voice-ai">Voice AI</option>
                      <option value="voice_pending" data-testid="billing-filter-voice-pending">Voice pending</option>
                      <option value="chat">Chat Sessions</option>
                      <option value="payment">Payments</option>
                      <option value="transfer">Transfers</option>
                      <option value="purchase">Purchases</option>
                      <option value="usage">Usage</option>
                    </select>
                    <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase tracking-wider text-gray-500">From</label>
                  <input type="date" value={txStartDate} onChange={(e) => setTxStartDate(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-teal-400/50"
                    data-testid="tx-start-date" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase tracking-wider text-gray-500">To</label>
                  <input type="date" value={txEndDate} onChange={(e) => setTxEndDate(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-teal-400/50"
                    data-testid="tx-end-date" />
                </div>

                <button onClick={handleTxDateApply}
                  className="px-4 py-2 rounded-lg bg-teal-400/10 text-teal-400 text-sm font-medium border border-teal-400/20 hover:bg-teal-400/20 transition-colors"
                  data-testid="tx-apply-filter">
                  <Filter size={14} className="inline mr-1.5" />Apply
                </button>
                {(txStartDate || txEndDate) && (
                  <button onClick={handleTxDateClear}
                    className="px-3 py-2 rounded-lg bg-white/5 text-gray-400 text-sm hover:bg-white/10 transition-colors"
                    data-testid="tx-clear-filter">Clear</button>
                )}
              </div>

              {/* Total spend banner — reflects the currently-applied filters
                  (account + type + date). Server-computed across all
                  matching records, not just the visible page. Shows
                  "$X.XXXX across N usage events" so users see exactly what
                  they're consuming under the slice they picked. */}
              {!txLoading && (
                <div
                  className="flex flex-wrap items-center justify-between gap-3 mb-5 p-3.5 rounded-xl bg-gradient-to-r from-teal-400/[0.06] to-violet-400/[0.04] border border-teal-400/15"
                  data-testid="tx-usage-total-banner"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-teal-400/15 flex items-center justify-center">
                      <TrendingUp size={16} className="text-teal-400" />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-teal-300/70 leading-tight">
                        {txFilter === 'all'
                          ? 'Total usage spend (filtered)'
                          : `Spend for ${subAccounts.find(s => s.sub_account_id === txFilter)?.name || 'this sub-account'}`}
                      </p>
                      <p className="text-lg font-semibold text-white tabular-nums leading-tight" data-testid="tx-usage-total-amount">
                        ${txUsageTotal.toFixed(txUsageTotal > 0 && txUsageTotal < 0.01 ? 4 : 2)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 leading-tight">Usage events</p>
                    <p className="text-base font-medium text-gray-200 tabular-nums leading-tight" data-testid="tx-usage-total-count">
                      {txUsageCount.toLocaleString()}
                    </p>
                  </div>
                </div>
              )}

              {txLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="animate-spin text-teal-400" size={24} />
                </div>
              ) : transactions.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="tx-table">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left text-xs text-gray-500 font-medium pb-3 pr-4">Date</th>
                          <th className="text-left text-xs text-gray-500 font-medium pb-3 pr-4">Type</th>
                          <th className="text-left text-xs text-gray-500 font-medium pb-3 pr-4">Description</th>
                          {txFilter === 'all' && (
                            <th className="text-left text-xs text-gray-500 font-medium pb-3 pr-4">Account</th>
                          )}
                          <th className="text-right text-xs text-gray-500 font-medium pb-3">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {transactions.map((tx, idx) => (
                          <tr key={idx} className="hover:bg-white/[0.03] transition-colors" data-testid={`tx-row-${idx}`}>
                            <td className="py-3 pr-4 text-sm text-gray-400 whitespace-nowrap">
                              {tx.timestamp ? new Date(tx.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '---'}
                            </td>
                            <td className="py-3 pr-4">{getTypeBadge(tx.type)}</td>
                            <td className="py-3 pr-4 text-sm text-white max-w-xs truncate">{tx.description || '---'}</td>
                            {txFilter === 'all' && (
                              <td className="py-3 pr-4 text-xs text-gray-500">
                                {tx.sub_account_name || (tx.entity_type === 'workspace' ? 'Agency' : tx.sub_account_id?.slice(0, 12) || '---')}
                              </td>
                            )}
                            <td className={`py-3 text-sm font-semibold text-right whitespace-nowrap`} data-testid={`tx-amount-${idx}`}>
                              {(() => {
                                const a = Number(tx.amount || 0);
                                const wb = typeof tx.would_be_amount === 'number' ? tx.would_be_amount : a;
                                const isUnlimited = tx.is_unlimited || (a === 0 && wb < 0);
                                const fmt = (v) => {
                                  const abs = Math.abs(v);
                                  if (abs === 0) return '$0.00';
                                  const decimals = abs > 0 && abs < 0.01 ? 4 : 2;
                                  return `$${abs.toFixed(decimals)}`;
                                };
                                if (isUnlimited) {
                                  return (
                                    <span className="inline-flex items-center gap-1.5">
                                      <span className="text-teal-400/80 tabular-nums">~{fmt(wb)}</span>
                                      <span className="text-[9px] uppercase tracking-wider font-bold text-teal-400/50 px-1 py-[1px] rounded border border-teal-400/20">unlimited</span>
                                    </span>
                                  );
                                }
                                if (a > 0)  return <span className="text-emerald-400 tabular-nums">+{fmt(a)}</span>;
                                if (a < 0)  return <span className="text-rose-400 tabular-nums">-{fmt(a)}</span>;
                                return <span className="text-gray-500 tabular-nums">$0.00</span>;
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {txTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                      <p className="text-xs text-gray-500">
                        Showing {txPage * TX_PAGE_SIZE + 1}–{Math.min((txPage + 1) * TX_PAGE_SIZE, txTotal)} of {txTotal}
                      </p>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleTxPageChange(txPage - 1)} disabled={txPage === 0}
                          className="px-3 py-1.5 rounded-lg text-xs bg-white/5 text-gray-400 hover:bg-white/10 disabled:opacity-30 transition-colors"
                          data-testid="tx-prev-page">Previous</button>
                        <span className="text-xs text-gray-500">Page {txPage + 1} of {txTotalPages}</span>
                        <button onClick={() => handleTxPageChange(txPage + 1)} disabled={txPage >= txTotalPages - 1}
                          className="px-3 py-1.5 rounded-lg text-xs bg-white/5 text-gray-400 hover:bg-white/10 disabled:opacity-30 transition-colors"
                          data-testid="tx-next-page">Next</button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-10">
                  <TrendingUp className="mx-auto text-gray-600 mb-3" size={36} />
                  <p className="text-gray-400">No transactions found</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {txStartDate || txEndDate ? 'Try adjusting your date range' : 'Transactions will appear here as balance are used'}
                  </p>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>

      {/* Top Up Modal */}
      <AnimatePresence>
        {showRatesModal && (
          <UsageRatesModal api={api} workspaceId={workspaceId} onClose={() => setShowRatesModal(false)} />
        )}

        {showTopUpModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => !processingPayment && setShowTopUpModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-3xl rounded-2xl overflow-hidden relative"
              onClick={e => e.stopPropagation()}
              style={{ background: 'radial-gradient(120% 120% at 0% 0%, #1a1d24 0%, #0d0f13 55%, #080a0e 100%)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              {/* Ambient corner washes */}
              <div aria-hidden className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-25 blur-3xl"
                   style={{ background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)' }} />
              <div aria-hidden className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full opacity-15 blur-3xl"
                   style={{ background: 'radial-gradient(circle, #10b981 0%, transparent 70%)' }} />

              <div className="relative px-7 pt-7 pb-5 border-b border-white/[0.05]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center shadow-lg"
                         style={{ background: 'linear-gradient(135deg,#fbbf24,#f59e0b)' }}>
                      <Zap size={22} className="text-[#0a0b0f]" fill="#0a0b0f" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold text-white tracking-tight">Purchase Balance</h3>
                      <p className="text-sm text-slate-400">Load your workspace — pay-as-you-go, balance never expires</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowTopUpModal(false)}
                    disabled={processingPayment}
                    className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-50"
                    data-testid="topup-modal-close"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="relative p-7">
                {/* Bundle grid — 3×2, Emergent-style */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                  {[
                    { amt: 10,  label: '$10',  desc: 'Get started' },
                    { amt: 25,  label: '$25',  desc: 'Light use' },
                    { amt: 50,  label: '$50',  desc: 'Recommended', popular: false },
                    { amt: 100, label: '$100', desc: 'Most popular', popular: true },
                    { amt: 250, label: '$250', desc: 'Power user' },
                    { amt: 500, label: '$500', desc: 'Agency scale' },
                  ].map(({ amt, label, desc, popular }, i) => {
                    const isProcessing = processingPayment && selectedPackage?.amount === amt;
                    return (
                      <motion.button
                        key={amt}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.04 }}
                        onClick={() => handleTopUp({ id: `bundle_${amt}`, amount: amt, price: amt })}
                        disabled={processingPayment}
                        className={`group relative rounded-2xl p-4 text-left transition-all overflow-hidden border ${
                          popular
                            ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/[0.08] to-amber-500/[0.03] shadow-[0_0_40px_-10px_rgba(251,191,36,0.35)] hover:shadow-[0_0_55px_-8px_rgba(251,191,36,0.5)]'
                            : 'border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-white/[0.01] hover:border-teal-400/35 hover:from-teal-400/[0.06]'
                        } disabled:opacity-50`}
                        data-testid={`package-${amt}`}
                      >
                        {popular && (
                          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-extrabold tracking-wider bg-gradient-to-r from-amber-400 to-orange-400 text-[#0a0b0f]">
                            POPULAR
                          </div>
                        )}
                        <div className="mb-3">
                          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{desc}</p>
                        </div>
                        <div className="flex items-baseline gap-1 mb-3">
                          <span className={`text-3xl font-bold ${popular ? 'text-amber-300' : 'text-white'}`}>{label}</span>
                          <span className="text-xs text-slate-500">balance</span>
                        </div>
                        <div className="text-xs text-emerald-400 font-medium mb-3">
                          = ${amt.toFixed(2)} credit
                        </div>
                        <div className={`inline-flex items-center justify-center w-full gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold transition-all ${
                          popular
                            ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-[#0a0b0f] group-hover:brightness-110'
                            : 'bg-white text-[#0a0b0f] group-hover:bg-slate-100'
                        }`}>
                          {isProcessing ? <RefreshCw size={13} className="animate-spin" /> : <Zap size={12} fill="currentColor" />}
                          {isProcessing ? 'Opening…' : 'Buy Now'}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>

                {/* Custom amount */}
                <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Custom amount</p>
                    <span className="text-[10px] text-slate-500">Min $10</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 flex items-center bg-[#0d0e12] border border-white/[0.08] rounded-lg overflow-hidden focus-within:border-teal-400/40">
                      <span className="px-3 text-slate-500 text-sm">$</span>
                      <input
                        type="number"
                        min="10"
                        step="1"
                        placeholder="Enter amount (min $10)"
                        className="flex-1 bg-transparent py-2.5 pr-3 text-white text-sm outline-none placeholder-slate-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        data-testid="custom-amount-input"
                        id="custom-topup-input"
                        onInput={(e) => { setCustomAmount(e.target.value); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const val = parseFloat(e.target.value);
                            if (val >= 10) handleTopUp({ id: 'custom', amount: val, price: val });
                            else toast.error('Minimum amount is $10');
                          }
                        }}
                      />
                    </div>
                    <button
                      onClick={() => {
                        const val = parseFloat(document.getElementById('custom-topup-input')?.value);
                        if (val >= 10) handleTopUp({ id: 'custom', amount: val, price: val });
                        else toast.error('Minimum amount is $10');
                      }}
                      disabled={processingPayment}
                      className="px-5 py-2.5 rounded-lg bg-white text-[#0a0b0f] text-sm font-semibold hover:bg-slate-100 transition-colors disabled:opacity-50"
                      data-testid="custom-topup-btn"
                    >
                      Buy {customAmount && parseFloat(customAmount) >= 10 ? `$${parseFloat(customAmount).toFixed(0)}` : ''}
                    </button>
                  </div>
                </div>

                {/* Auto Top-up — simple "Configure" trigger */}
                <div className="flex items-center justify-between rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-teal-400/10 border border-teal-400/20 flex items-center justify-center">
                      <RefreshCw className="text-teal-400" size={17} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">Auto Top-up</p>
                      <p className="text-[11px] text-slate-500">
                        {savedAutoTopOff?.enabled
                          ? `Enabled — refills $${savedAutoTopOff.package_id === 'custom' ? savedAutoTopOff.custom_amount : BALANCE_PACKAGES.find(p => p.id === savedAutoTopOff.package_id)?.amount ?? savedAutoTopOff.amount ?? '—'} when balance < $${savedAutoTopOff.threshold}`
                          : 'Automatically refill balance when low'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowTopUpModal(false); setAutoTopOff({ ...savedAutoTopOff }); setShowAutoTopOffModal(true); }}
                    className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      savedAutoTopOff?.enabled
                        ? 'bg-teal-400/20 text-teal-300 hover:bg-teal-400/30 border border-teal-400/30'
                        : 'bg-white text-[#0a0b0f] hover:bg-slate-100'
                    }`}
                    data-testid="configure-auto-topup-btn"
                  >
                    {autoTopOff?.enabled ? 'Edit' : 'Configure'}
                  </button>
                </div>

                <p className="text-[11px] text-slate-500 text-center mt-5">
                  Secure payment via Stripe · Balance never expires · Cancel auto-refill anytime
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Auto Top-Off Modal */}
        {showAutoTopOffModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAutoTopOffModal(false)}
            data-testid="auto-topoff-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-md max-h-[85dvh] bg-space-800 rounded-xl overflow-y-auto"
              onClick={e => e.stopPropagation()}
              role="dialog" aria-modal="true" aria-labelledby="billing-auto-pay-title"
              data-testid="auto-topoff-dialog"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-teal-400/10 flex items-center justify-center">
                      <RefreshCw className="text-teal-400" size={20} />
                    </div>
                    <div>
                      <h3 id="billing-auto-pay-title" className="text-lg font-semibold text-white">Auto Top-off</h3>
                      <p className="text-sm text-gray-400">Automatically add balance when low</p>
                    </div>
                  </div>
                  <button onClick={() => setShowAutoTopOffModal(false)} aria-label="Close auto-pay settings" data-testid="auto-topoff-modal-close" className="p-2 rounded-lg text-gray-400 hover:bg-white/10">
                    <X size={18} />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div>
                    <p className="text-sm font-medium text-white">Enable Auto Top-off</p>
                    <p className="text-xs text-gray-500 mt-1">Automatically charge when balance run low</p>
                  </div>
                  <button
                    onClick={() => setAutoTopOff({ ...autoTopOff, enabled: !autoTopOff.enabled })}
                    className={autoTopOff.enabled ? 'text-teal-400' : 'text-gray-500'}
                    data-testid="auto-topoff-toggle"
                  >
                    {autoTopOff.enabled ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                  </button>
                </div>

                {autoTopOff.enabled && (
                  <>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Top up when balance falls below</label>
                      <select
                        value={autoTopOff.threshold}
                        onChange={(e) => setAutoTopOff({ ...autoTopOff, threshold: parseInt(e.target.value) })}
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-teal-400/50"
                        data-testid="threshold-select"
                      >
                        {AUTO_TOPOFF_THRESHOLDS.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Balance to add when triggered</label>
                      <select
                        value={autoTopOff.package_id}
                        onChange={(e) => setAutoTopOff({ ...autoTopOff, package_id: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-teal-400/50"
                        data-testid="package-select"
                      >
                        {BALANCE_PACKAGES.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            ${pkg.amount.toFixed(2)}
                          </option>
                        ))}
                        <option value="custom">Custom amount…</option>
                      </select>
                      {autoTopOff.package_id === 'custom' && (
                        <div className="mt-3 relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                          <input
                            type="number"
                            min="1"
                            max="9999"
                            step="1"
                            placeholder="Enter amount (e.g. 75)"
                            value={autoTopOff.custom_amount ?? ''}
                            onChange={(e) => setAutoTopOff({ ...autoTopOff, custom_amount: e.target.value === '' ? null : Math.max(0, Math.min(9999, Number(e.target.value))) })}
                            className="w-full pl-8 pr-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-teal-400/50 tabular-nums"
                            data-testid="custom-topup-amount-input"
                          />
                          <p className="text-xs text-slate-500 mt-1.5">Must be between $1 and $9,999.</p>
                        </div>
                      )}
                    </div>

                    <div className="p-4 rounded-xl bg-teal-400/10 border border-teal-400/20">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="text-teal-400 flex-shrink-0" size={18} />
                        <div className="text-sm text-gray-300">
                          {(() => {
                            const pkg = BALANCE_PACKAGES.find(p => p.id === autoTopOff.package_id);
                            const amount = autoTopOff.package_id === 'custom'
                              ? Number(autoTopOff.custom_amount || 0)
                              : (pkg?.amount || 0);
                            const amountLabel = amount > 0 ? `$${amount.toFixed(2)}` : '—';
                            return (
                              <>
                                When your balance drops below <span className="text-white font-medium">${(autoTopOff.threshold || 10).toFixed(2)}</span>,
                                we&apos;ll automatically top it up with <span className="text-teal-400 font-semibold">{amountLabel}</span> and charge your card on file.
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex items-center gap-3 pt-4">
                  <button onClick={() => setShowAutoTopOffModal(false)} className="flex-1 px-4 py-3 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-colors">
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAutoTopOff}
                    disabled={savingAutoTopOff}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-teal-400 text-space-900 font-medium hover:bg-teal-500 transition-colors disabled:opacity-50"
                    data-testid="save-auto-topoff-btn"
                  >
                    {savingAutoTopOff && <RefreshCw className="animate-spin" size={16} />}
                    Save Settings
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Plan Change Modal */}
        {showPlanChangeModal && selectedPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
            onClick={() => setShowPlanChangeModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg glass-card rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white">Change Plan</h3>
                    <p className="text-sm text-gray-400">
                      {(selectedPlan.monthly || 0) > (billing?.current_plan?.price || 0) ? 'Upgrade' : 'Downgrade'} to {selectedPlan.name}
                    </p>
                  </div>
                  <button onClick={() => setShowPlanChangeModal(false)} className="p-2 rounded-lg text-gray-400 hover:bg-white/5">
                    <X size={20} />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-xs text-gray-500 mb-1">Current Plan</p>
                    <p className="text-lg font-semibold text-white">{billing?.current_plan?.name || 'Free'}</p>
                    <p className="text-sm text-gray-400">
                      {billing?.current_plan?.price > 0 ? `$${billing.current_plan.price}/mo` : '$0/month'}
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-teal-400/10 border border-teal-400/30">
                    <p className="text-xs text-teal-400 mb-1">New Plan</p>
                    <p className="text-lg font-semibold text-white">{selectedPlan.name}</p>
                    <p className="text-sm text-teal-400">
                      {selectedPlan.monthly > 0 ? `$${selectedPlan.monthly}/mo` : '$0/month'}
                    </p>
                  </div>
                </div>

                {getRequiredArchiveCount() > 0 && (
                  <div className="mb-6" data-testid="downgrade-archive-warning">
                    <div className="p-4 rounded-xl bg-amber-400/10 border border-amber-400/20 mb-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="text-amber-400 flex-shrink-0 mt-0.5" size={18} />
                        <div>
                          <p className="text-sm text-amber-400 font-medium">
                            Archive {getRequiredArchiveCount()} sub-account{getRequiredArchiveCount() > 1 ? 's' : ''} to downgrade
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            The {selectedPlan.name} plan allows {selectedPlan.subAccounts || 1} sub-account{(selectedPlan.subAccounts || 1) === 1 ? '' : 's'}.
                            You currently have {subAccounts.filter(s => s.status !== 'archived').length}. Pick which ones to archive — archived sub-accounts are fully locked (no calls, chats or access).
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {subAccounts.filter(s => s.status !== 'archived').map(sub => (
                        <label
                          key={sub.sub_account_id}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            subAccountsToArchive.includes(sub.sub_account_id)
                              ? 'bg-red-400/10 border border-red-400/30'
                              : 'bg-white/5 border border-white/10 hover:bg-white/10'
                          }`}
                        >
                          <input
                            type="checkbox"
                            data-testid={`downgrade-archive-checkbox-${sub.sub_account_id}`}
                            checked={subAccountsToArchive.includes(sub.sub_account_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSubAccountsToArchive([...subAccountsToArchive, sub.sub_account_id]);
                              } else {
                                setSubAccountsToArchive(subAccountsToArchive.filter(id => id !== sub.sub_account_id));
                              }
                            }}
                            className="w-4 h-4 rounded border-white/20"
                          />
                          <div className="flex-1">
                            <p className="text-sm text-white">{sub.name}</p>
                            <p className="text-xs text-gray-500">{sub.sub_account_id}</p>
                          </div>
                          <Archive size={14} className="text-gray-500" />
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Selected: {subAccountsToArchive.length} / {getRequiredArchiveCount()} required
                    </p>
                  </div>
                )}

                <div className="p-4 rounded-xl bg-white/5 mb-6">
                  <h4 className="text-sm font-medium text-white mb-2">What happens next:</h4>
                  <ul className="text-xs text-gray-400 space-y-1">
                    <li>&#8226; Paid changes are confirmed in secure checkout or the billing portal.</li>
                    <li>&#8226; Review the final charge there before confirming.</li>
                    <li>&#8226; Your usage wallet remains unchanged.</li>
                    {subAccountsToArchive.length > 0 && (
                      <li>&#8226; Archived accounts can be restored if you upgrade later</li>
                    )}
                  </ul>
                </div>

                <div className="flex items-center gap-3">
                  <button onClick={() => setShowPlanChangeModal(false)} className="flex-1 px-4 py-3 rounded-xl bg-white/5 text-white hover:bg-white/10 transition-colors">
                    Cancel
                  </button>
                  <button
                    onClick={handlePlanChange}
                    data-testid="plan-change-confirm-btn"
                    disabled={changingPlan || (getRequiredArchiveCount() > 0 && subAccountsToArchive.length < getRequiredArchiveCount())}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-teal-400 text-space-900 font-medium hover:bg-teal-500 transition-colors disabled:opacity-50"
                  >
                    {changingPlan && <RefreshCw className="animate-spin" size={16} />}
                    Confirm Change
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Allocate Balance Modal */}
        {showAllocateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAllocateModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="w-full max-w-md max-h-[85dvh] bg-space-800 rounded-xl overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="allocation-dialog"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Allocate Balance</h3>
                    <p className="text-sm text-gray-400">Transfer balance from your agency pool</p>
                  </div>
                  <button onClick={() => setShowAllocateModal(null)} aria-label="Close allocation" data-testid="allocation-modal-close" className="p-2 rounded-lg text-gray-400 hover:bg-white/10">
                    <X size={18} />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="p-3 rounded-lg bg-white/5 flex items-center justify-between">
                  <span className="text-sm text-gray-400">Your agency balance</span>
                  <span className="text-sm font-bold text-amber-400" data-testid="allocation-agency-balance">${balanceBalance.toFixed(2)}</span>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Balance to allocate</label>
                  <input
                    type="number"
                    min="1"
                    max={balanceBalance}
                    value={allocateAmount}
                    onChange={(e) => setAllocateAmount(e.target.value)}
                    placeholder="e.g. 1000"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-amber-400/50 placeholder:text-gray-600"
                    data-testid="allocate-amount-input"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">These balance will be deducted from your agency balance</p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowAllocateModal(null)} className="flex-1 px-4 py-3 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-colors">Cancel</button>
                  <button
                    onClick={() => handleAllocateBalance(showAllocateModal)}
                    disabled={allocating || !allocateAmount || parseFloat(allocateAmount) <= 0}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-amber-400 text-space-900 font-medium hover:bg-amber-500 transition-colors disabled:opacity-50"
                    data-testid="confirm-allocate-btn"
                  >
                    {allocating && <RefreshCw className="animate-spin" size={16} />}
                    Allocate
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Set Credit Limit Modal */}
        {showLimitModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowLimitModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="w-full max-w-md max-h-[85dvh] bg-space-800 rounded-xl overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="balance-limit-dialog"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Balance Usage Limit</h3>
                    <p className="text-sm text-gray-400">Control how much balance this sub-account can use</p>
                  </div>
                  <button onClick={() => setShowLimitModal(null)} aria-label="Close spending limit" data-testid="balance-limit-modal-close" className="p-2 rounded-lg text-gray-400 hover:bg-white/10">
                    <X size={18} />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {(() => {
                  const currentSa = billing?.sub_account_billing?.find(s => s.sub_account_id === showLimitModal);
                  const isUnl = !!currentSa?.balance_unlimited;
                  const currentCap = currentSa?.balance_limit;
                  const hasCap = !isUnl && currentCap != null;
                  return (
                <div className="space-y-3">
                  <button
                    onClick={() => handleSetCreditLimit(showLimitModal, true)}
                    disabled={savingLimit}
                    className={`w-full p-4 rounded-xl text-left transition-all ${
                      isUnl
                        ? 'bg-gradient-to-r from-amber-400/15 to-orange-400/10 border-2 border-amber-400/60 shadow-[0_0_24px_-4px_rgba(251,191,36,0.45)]'
                        : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-400/30'
                    }`}
                    data-testid="unlimited-btn"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <Infinity size={18} className={isUnl ? 'text-amber-300' : 'text-amber-400'} />
                        <div>
                          <p className={`text-sm font-semibold ${isUnl ? 'text-amber-300' : 'text-amber-400'}`}>
                            Unlimited {isUnl && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-300 font-bold uppercase tracking-wider">Active</span>}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">Sub-account uses agency pool freely with no cap</p>
                        </div>
                      </div>
                      {isUnl && <CheckCircle className="text-amber-300" size={22} />}
                    </div>
                  </button>

                  <div className={`p-4 rounded-xl transition-all ${
                    hasCap
                      ? 'bg-gradient-to-r from-teal-400/10 to-cyan-400/5 border-2 border-teal-400/50 shadow-[0_0_18px_-4px_rgba(45,212,191,0.35)]'
                      : 'bg-white/5 border border-white/10'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-white">Set a balance cap</p>
                      {hasCap && (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-teal-400/20 text-teal-300 font-bold uppercase tracking-wider flex items-center gap-1">
                          <CheckCircle size={10} /> ${Number(currentCap).toLocaleString()} cap
                        </span>
                      )}
                    </div>
                    <input
                      type="number"
                      min="0"
                      value={limitValue}
                      onChange={(e) => setLimitValue(e.target.value)}
                      placeholder="e.g. 100"
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-teal-400/50 placeholder:text-gray-600 mb-3"
                      data-testid="limit-value-input"
                      autoFocus
                    />
                    <p className="text-xs text-gray-500 mb-3">Sub-account can use up to this dollar amount. Usage is blocked once depleted.</p>
                    <button
                      onClick={() => handleSetCreditLimit(showLimitModal, false)}
                      disabled={savingLimit || !limitValue}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-teal-400 text-space-900 font-medium hover:bg-teal-500 transition-colors disabled:opacity-50"
                      data-testid="set-limit-btn"
                    >
                      {savingLimit && <RefreshCw className="animate-spin" size={16} />}
                      {hasCap ? 'Update Cap' : 'Set Limit'}
                    </button>
                  </div>
                </div>
                  );
                })()}
                <button onClick={() => setShowLimitModal(null)} className="w-full px-4 py-3 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 transition-colors">Cancel</button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Sub-Account Auto Top-off Modal */}
        {showSaAutoTopOff && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowSaAutoTopOff(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="w-full max-w-md max-h-[85dvh] bg-space-800 rounded-xl overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="sub-account-auto-topoff-dialog"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Sub-Account Auto Top-off</h3>
                    <p className="text-sm text-gray-400">Automatically allocate balance on a schedule</p>
                  </div>
                  <button onClick={() => setShowSaAutoTopOff(null)} aria-label="Close account auto-funding" data-testid="sub-account-auto-topoff-close" className="p-2 rounded-lg text-gray-400 hover:bg-white/10">
                    <X size={18} />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-5">
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div>
                    <p className="text-sm font-medium text-white">Enable Auto Top-off</p>
                    <p className="text-xs text-gray-500 mt-1">Periodically allocate balance from your pool</p>
                  </div>
                  <button
                    onClick={() => setSaAutoTopOff({ ...saAutoTopOff, enabled: !saAutoTopOff.enabled })}
                    className={saAutoTopOff.enabled ? 'text-teal-400' : 'text-gray-500'}
                    data-testid="sa-auto-topoff-toggle"
                  >
                    {saAutoTopOff.enabled ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                  </button>
                </div>

                {saAutoTopOff.enabled && (
                  <>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Balance to allocate each period</label>
                      <input
                        type="number"
                        min="100"
                        value={saAutoTopOff.amount || ''}
                        onChange={(e) => setSaAutoTopOff({ ...saAutoTopOff, amount: parseInt(e.target.value) || 0 })}
                        placeholder="e.g. 1000"
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-teal-400/50 placeholder:text-gray-600"
                        data-testid="sa-topoff-amount"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Top-off period</label>
                      <div className="grid grid-cols-2 gap-3">
                        {[15, 30].map(days => (
                          <button
                            key={days}
                            onClick={() => setSaAutoTopOff({ ...saAutoTopOff, period_days: days })}
                            className={`p-3 rounded-xl text-center transition-all ${
                              saAutoTopOff.period_days === days
                                ? 'bg-teal-400/10 border-2 border-teal-400/50 text-teal-400'
                                : 'bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10'
                            }`}
                            data-testid={`sa-topoff-period-${days}`}
                          >
                            <p className="text-lg font-bold">{days}</p>
                            <p className="text-xs">days</p>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="p-4 rounded-xl bg-teal-400/10 border border-teal-400/20">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="text-teal-400 flex-shrink-0" size={18} />
                        <p className="text-sm text-gray-300">
                          Every <span className="text-white font-medium">{saAutoTopOff.period_days} days</span>,{' '}
                          <span className="text-teal-400 font-medium">{(saAutoTopOff.amount || 0).toLocaleString()} balance</span>{' '}
                          will be allocated from your agency pool.
                        </p>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <button onClick={() => setShowSaAutoTopOff(null)} className="flex-1 px-4 py-3 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-colors">Cancel</button>
                  <button
                    onClick={() => handleSaveSaAutoTopOff(showSaAutoTopOff)}
                    disabled={savingSaAutoTopOff}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-teal-400 text-space-900 font-medium hover:bg-teal-500 transition-colors disabled:opacity-50"
                    data-testid="save-sa-auto-topoff"
                  >
                    {savingSaAutoTopOff && <RefreshCw className="animate-spin" size={16} />}
                    Save Settings
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Cancel Subscription Modal */}
        {showCancelModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCancelModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="w-full max-w-md bg-space-800 rounded-xl overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">Cancel Subscription</h3>
                <p className="text-sm text-gray-400 mt-1">Your access continues until the end of your current billing period.</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="p-3 rounded-lg bg-amber-400/10 border border-amber-400/20">
                  <p className="text-xs text-amber-400">Your balance balance will be preserved. You can reactivate anytime.</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Reason (optional)</label>
                  <textarea
                    value={cancelReason}
                    onChange={e => setCancelReason(e.target.value)}
                    placeholder="Help us improve..."
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-red-400/50 resize-none"
                    data-testid="cancel-reason"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => setShowCancelModal(false)} className="flex-1 px-4 py-2.5 rounded-lg bg-white/5 text-white text-sm hover:bg-white/10 transition-colors">
                    Keep Plan
                  </button>
                  <button
                    onClick={handleCancelPlan}
                    disabled={cancelling}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-red-500/10 text-red-400 text-sm font-medium border border-red-500/20 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                    data-testid="confirm-cancel-btn"
                  >
                    {cancelling && <RefreshCw className="animate-spin" size={14} />}
                    Cancel Subscription
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top-up success celebration */}
      {topUpCelebration && (
        <TopUpCelebration
          amount={topUpCelebration.amount}
          isFirstTopup={topUpCelebration.isFirstTopup}
          onClose={() => setTopUpCelebration(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default BillingPage;
